export const isBlank = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) return value.length === 0 || value.every(isBlank);
    return false;
};

export const hasNonEmptyArray = (value) => Array.isArray(value) && value.some((item) => !isBlank(item));

export const hasSelectedBoolean = (value) => value === true || value === false || value === 'yes' || value === 'no';

export const hasRequiredComment = (comments) => (
    Array.isArray(comments) &&
    comments.some((item) => !isBlank(item?.comment || item?.Comment || item))
);

export const hasUploadedFile = (files) => {
    if (!files) return false;
    if (files instanceof FileList) return files.length > 0;
    if (typeof files === 'object' && !Array.isArray(files)) {
        return Object.values(files).some(hasUploadedFile);
    }
    return Array.isArray(files) && files.length > 0;
};

export const toNullableBoolFromYesNo = (value) => {
    if (value === 'yes') return true;
    if (value === 'no') return false;
    if (value === true || value === false) return value;
    return null;
};

export const toNullableBool = (value) => {
    if (value === true || value === false) return value;
    if (value === 'true') return true;
    if (value === 'false') return false;
    if (value === 'yes') return true;
    if (value === 'no') return false;
    return null;
};

const requireField = (errors, formData, field, message) => {
    if (isBlank(formData[field])) {
        errors[field] = message || 'Bu alan zorunludur.';
    }
};

const requireBoolean = (errors, formData, field, message) => {
    if (!hasSelectedBoolean(formData[field])) {
        errors[field] = message || 'Bu alan zorunludur.';
    }
};

const requireArray = (errors, value, field, message) => {
    if (!hasNonEmptyArray(value)) {
        errors[field] = message || 'Bu alan zorunludur.';
    }
};

const isState = (currentStateCode, codes) => codes.includes(currentStateCode);

const normalizeAction = (action) => (action || 'pending').toLowerCase();

const isAction = (action, actions) => actions.includes(normalizeAction(action));

export const getSelectedProviderId = (providerValue) => {
    if (Array.isArray(providerValue)) return providerValue.find((item) => !isBlank(item)) || '';
    return providerValue || '';
};

export const isNoInsuranceProvider = (providerId, insuranceCompanies = []) => {
    const selectedId = getSelectedProviderId(providerId);
    if (isBlank(selectedId)) return false;

    const provider = insuranceCompanies.find((company) => company?.id === selectedId);
    const providerName = String(provider?.name || provider?.title || '').trim().toUpperCase();
    const ibanNumber = String(provider?.ibanNumber || provider?.IBANNumber || '').trim().toUpperCase();

    return providerName === 'YOK' || ibanNumber === 'YOK';
};

export const shouldRequirePolicyDetails = (policy, insuranceCompanies = [], providerField = 'trafficInsuranceProvider') => {
    const providerId = getSelectedProviderId(policy?.[providerField]);
    return !isBlank(providerId) && !isNoInsuranceProvider(providerId, insuranceCompanies);
};

export const CALL_FORM_STATES = {
    powerOfAttorney: ['Vekalet Bekleniyor'],
    documentRequest: ['Evrak Bekleniyor'],
    expert: ['Eksper İşlem Bekleniyor', 'Yeni Eksper İşlem Bekleniyor'],
    expertReport: ['Eksper Rapor Bekleniyor', 'Yeni Eksper Rapor Bekleniyor', 'Eksper Raporu Ödeme Bekleniyor', 'Yeni Eksper Raporu Ödeme Bekleniyor'],
    insuranceApplication: ['Sigorta Başvuru Dilekçe Bekleniyor', 'Yeni Sigorta Başvuru Dilekçe Bekleniyor', 'Ekspersiz Yeni Sigorta Başvuru Dilekçe Bekleniyor'],
};

export const validateMandatoryCallFormFields = (formData, { currentStateCode, action, insuranceCompanies = [] } = {}) => {
    const errors = {};
    const isCorporate = formData.customerType === 'Kurumsal';
    const shouldValidateBase = isAction(action, ['save']);
    const shouldCompleteCurrentStep = isAction(action, ['received']);
    const shouldCompleteExpert = isAction(action, ['next']);
    const shouldValidateInsuranceApplication = isAction(action, ['next', 'withoutexpert']);

    if (shouldValidateBase) {
        requireField(errors, formData, isCorporate ? 'companyName' : 'clientName', 'Müvekkil adı zorunludur.');
        requireField(errors, formData, isCorporate ? 'taxNumber' : 'tcid', isCorporate ? 'Vergi numarası zorunludur.' : 'TC kimlik numarası zorunludur.');
        requireField(errors, formData, 'reference', 'Referans zorunludur.');
        requireField(errors, formData, 'phoneDescription', 'Ana telefon açıklaması zorunludur.');
        requireField(errors, formData, 'phoneNumber', 'Ana telefon numarası zorunludur.');
        requireField(errors, formData, 'licensePlate', 'Plaka zorunludur.');
        requireField(errors, formData, 'customerContractRate', 'Sözleşme oranı zorunludur.');
        requireField(errors, formData, 'accidentDate', 'Kaza tarihi zorunludur.');
        requireField(errors, formData, 'customerEnforcementAttorneyFeeShareRate', 'İcra vekalet hakediş oranı zorunludur.');
        requireField(errors, formData, 'customerJudgmentAttorneyFeeShareRate', 'İlam vekalet hakediş oranı zorunludur.');
        requireBoolean(errors, formData, 'customerPayInterestToCustomer', 'Müşteriye faiz ödenecek mi seçimi zorunludur.');
    }

    if (shouldCompleteCurrentStep && isState(currentStateCode, CALL_FORM_STATES.powerOfAttorney)) {
        requireField(errors, formData, 'powerOfAttorneyDate', 'Vekalet tarihi zorunludur.');
        requireBoolean(errors, formData, 'isPowerOfAttorneyTimed', 'Vekalet süreli mi seçimi zorunludur.');
        requireBoolean(errors, formData, 'isLicensePlatesLimited', 'Plaka sınırlaması var mı seçimi zorunludur.');
        requireBoolean(errors, formData, 'hasOriginalArrived', 'Vekalet aslı geldi mi seçimi zorunludur.');
    }

    if (shouldCompleteCurrentStep && isState(currentStateCode, CALL_FORM_STATES.documentRequest)) {
        requireBoolean(errors, formData, 'isThereOpenDamageFile', 'Açık hasar dosyası var mı seçimi zorunludur.');
        requireField(errors, formData, 'documentRequestDate', 'Evrak talep tarihi zorunludur.');
        requireField(errors, formData, 'documentRequestStatus', 'Evrak talep durumu zorunludur.');
        if (formData.documentRequestStatus === 'Evraklar Geldi') {
            requireField(errors, formData, 'documentReceivedDate', 'Evrak gelme tarihi zorunludur.');
        }
        requireField(errors, formData, 'counterpartyLicensePlateNumber', 'Karşı taraf plakası zorunludur.');
        const counterpartyTrafficPolicy = formData.counterpartyTrafficInsurancePolicy || {};
        requireArray(errors, counterpartyTrafficPolicy.trafficInsuranceProvider, 'counterpartyTrafficInsuranceProvider', 'Karşı taraf trafik sigorta sağlayıcısı zorunludur.');
        if (shouldRequirePolicyDetails(counterpartyTrafficPolicy, insuranceCompanies)) {
            requireField(errors, counterpartyTrafficPolicy, 'trafficInsuranceNumber', 'Karşı taraf trafik poliçe no zorunludur.');
            requireField(errors, counterpartyTrafficPolicy, 'trafficInsuranceStart', 'Karşı taraf trafik poliçe başlangıcı zorunludur.');
            requireField(errors, counterpartyTrafficPolicy, 'trafficInsuranceExpiry', 'Karşı taraf trafik poliçe vadesi zorunludur.');
            if (errors.trafficInsuranceNumber) errors.counterpartyTrafficInsuranceNumber = errors.trafficInsuranceNumber;
            if (errors.trafficInsuranceStart) errors.counterpartyTrafficInsuranceStart = errors.trafficInsuranceStart;
            if (errors.trafficInsuranceExpiry) errors.counterpartyTrafficInsuranceExpiry = errors.trafficInsuranceExpiry;
            delete errors.trafficInsuranceNumber;
            delete errors.trafficInsuranceStart;
            delete errors.trafficInsuranceExpiry;
        }
    }

    if (shouldCompleteExpert && isState(currentStateCode, CALL_FORM_STATES.expert)) {
        requireField(errors, formData, 'whoseInsurance', 'Başvuru yapılacak yer zorunludur.');
        requireArray(errors, formData.filesToBeWorked, 'filesToBeWorked', 'Dosya türü zorunludur.');
    }

    if (shouldCompleteExpert && isState(currentStateCode, CALL_FORM_STATES.expertReport) && !hasUploadedFile(formData.expertReportFiles)) {
        errors.expertReportFiles = 'Eksper raporu dosyası yükleme zorunludur.';
    }

    if (shouldValidateInsuranceApplication && isState(currentStateCode, CALL_FORM_STATES.insuranceApplication)) {
        const hasInsuranceApplicationType = !isBlank(formData.insuranceApplicationType) ||
            (formData.filesToBeWorked || []).some((item) => !isBlank(item?.insuranceApplicationType));
        const hasInsuranceApplicationDate = !isBlank(formData.insuranceApplicationDate) ||
            (formData.filesToBeWorked || []).some((item) => !isBlank(item?.insuranceApplicationDate));

        if (!hasInsuranceApplicationType) {
            errors.insuranceApplicationType = 'Sigorta başvuru türü zorunludur.';
        }
        if (!hasInsuranceApplicationDate) {
            errors.insuranceApplicationDate = 'Sigorta başvuru tarihi zorunludur.';
        }
        if (!hasUploadedFile(formData.insuranceApplicationFiles)) {
            errors.insuranceApplicationFiles = 'Sigorta başvuru dilekçesi yükleme zorunludur.';
        }
    }

    return errors;
};
